import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress
} from "@mui/material";
import { useTheme } from '../context/ThemeContext';
import CloseIcon from '@mui/icons-material/Close';
import './ScanPanel.css';
import PressureScanViewer from './NewPressureScan';

export interface PressureScanner {
  cleanup: () => void;
  setDarkMode?: (isDarkMode: boolean) => void;
}

interface ScanPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ScanPanel: React.FC<ScanPanelProps> = ({ isOpen, onClose }) => {
  const { isDarkMode } = useTheme();
  const scannerRef = useRef<PressureScanner | null>(null);
  const [animationClass, setAnimationClass] = useState('');
  const [saveTrigger, setSaveTrigger] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAnimationClass('scan-panel-visible');
    } else {
      setAnimationClass('scan-panel-hidden');
    }
  }, [isOpen]);

   const handleSaveClick = () => {
        setSaveTrigger(true); 
    };

  const handleNewTest = async () => {
    if (scannerRef.current) {
      scannerRef.current.cleanup();
    }

    // const container = document.getElementById('scan-container');
    // if (!container) {
    //   console.error('Container element not found');
    //   return;
    // }
  };

  const handleStopTest = () => {
    if (scannerRef.current) {
      scannerRef.current.cleanup();
      scannerRef.current = null;
    }

    // const container = document.getElementById('scan-container');
    // if (container) {
    //   container.innerHTML = '';
    // }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.cleanup();
      }
    };
  }, []);

  // Update dark mode
  useEffect(() => {
    if (scannerRef.current && scannerRef.current.setDarkMode) {
      scannerRef.current.setDarkMode(isDarkMode);
    }
  }, [isDarkMode]);

  // Auto-start scanning when modal opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        handleNewTest();
      }, 0);
      return () => clearTimeout(timer);
    } else {
      handleStopTest();
    }
  }, [isOpen]);

  if (!isOpen && animationClass === 'scan-panel-hidden') {
    return null;
  }


  return (
  <Modal open={isOpen} onClose={onClose} closeAfterTransition sx={{display: "flex", alignItems: "center", justifyContent: "center",}}>
    <Box
      sx={{
        bgcolor: isDarkMode ? '#333' : '#fff',
        color: isDarkMode ? '#fff' : '#000',
        borderRadius: 2,
        width: '50vw',
        height: '80vh',
        mx: 'auto',
        boxShadow: 24,
        outline: 'none',
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #ccc' }}>
        <Typography variant="h6" sx={{ color: isDarkMode ? '#fff' : '#076596' }}>
          Pressure Scan
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: isDarkMode ? '#fff' : '#000' }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 3,
          position: 'relative',
        }}
      >
        {isOpen && (
          <PressureScanViewer
            isDarkMode={isDarkMode}
            onAccept={() => onClose()}
            onClose={() => onClose()}
            triggerSave={saveTrigger}
            resetSaveTrigger={() => setSaveTrigger(false)}
          />
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #ccc', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            color: isDarkMode ? '#fff' : '#000',
            borderColor: '#aaa',
            width: '80px',
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            color: '#fff',
            backgroundColor: '#076596',
            width: '80px',
            fontWeight: 500,
            px: 4,
            '&:hover': { backgroundColor: '#0a6fb4' },
          }}
          onClick={handleSaveClick}
        >
          Save
        </Button>
      </Box>

      {/* Loading Overlay */}
      {false && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(212, 212, 212, 0.47)',
            zIndex: 50,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          <CircularProgress sx={{ color: '#076596' }} />
        </Box>
      )}
    </Box>
  </Modal>
);
};

export default ScanPanel;